// Constants for the Compose Email modal.

export const COMPOSE_EMAIL_BODY_PLACEHOLDER = '';

// Host element id the PlateEditor portals its formatting toolbar into. The
// matching <div id> is rendered as a sticky strip at the top of the scroll body.
export const COMPOSE_TOOLBAR_HOST_ID = 'composeToolbarHost';

// Upload-source tags threaded to PlateEditor / processImagesBeforeSave so
// pasted/dropped images are bucketed by context on the backend.
export const COMPOSE_EMAIL_UPLOAD_SOURCE = {
    COMPANY: 'compose-email',
    LENDER: 'compose-email_lender',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Attachment / signature redesign (RECUR-23138)
// ─────────────────────────────────────────────────────────────────────────────

// System attachment keys. These map 1:1 to the boolean flags sent in the
// notification payload (attachDataPendingList / attachEngagementLetter /
// attachBureauKycConsent). Manual uploads are tracked separately by docId.
export enum SystemAttachmentKey {
    DATA_PENDING = 'DATA_PENDING',
    ENGAGEMENT_LETTER = 'ENGAGEMENT_LETTER',
    BUREAU_KYC_CONSENT = 'BUREAU_KYC_CONSENT',
    ONE_PAGER = 'ONE_PAGER',
}

// Human-readable labels for each system attachment row / pill.
export const SYSTEM_ATTACHMENT_LABELS: Record<SystemAttachmentKey, string> = {
    [SystemAttachmentKey.DATA_PENDING]: 'Data Pending List',
    [SystemAttachmentKey.ENGAGEMENT_LETTER]: 'Engagement Letter',
    [SystemAttachmentKey.BUREAU_KYC_CONSENT]: 'Bureau & KYC Consent',
    [SystemAttachmentKey.ONE_PAGER]: 'One Pager',
};

// Stable render order for the toggle rows and the system pills:
// Data Pending → Engagement Letter → Bureau & KYC Consent → One Pager, then manual uploads.
export const SYSTEM_ATTACHMENT_ORDER: SystemAttachmentKey[] = [
    SystemAttachmentKey.DATA_PENDING,
    SystemAttachmentKey.ENGAGEMENT_LETTER,
    SystemAttachmentKey.BUREAU_KYC_CONSENT,
    SystemAttachmentKey.ONE_PAGER,
];

// Document-backed system attachments: each is a saved Plate template (fetched
// by docType) the user reviews/edits in its own drawer; on Save & Close the
// drawer is captured to a PDF, uploaded, and attached as a docId (view-to-attach,
// RECUR-23138). DATA_PENDING is intentionally absent — it is a table rendered
// into the email body, not a standalone document.
export const DOC_TYPE_SYSTEM_KEYS: SystemAttachmentKey[] = [
    SystemAttachmentKey.ENGAGEMENT_LETTER,
    SystemAttachmentKey.BUREAU_KYC_CONSENT,
    SystemAttachmentKey.ONE_PAGER,
];

// `source` value the drawers (EngagementLetter / BureauKycConsent / OnePager)
// are opened with when the trigger is Compose Email — gates both their mount
// inside the modal subtree and the post-save view-to-attach capture.
export const COMPOSE_EMAIL_DRAWER_SOURCE = 'COMPOSE_EMAIL';

// accountType used by the template-availability fetches. EngagementLetterV2 /
// BureauKycConsent both call their template helpers with this value.
export const ATTACHMENT_TEMPLATE_ACCOUNT_TYPE = 'INVESTOR';

// Max characters shown on an attachment pill label before it is truncated.
export const ATTACHMENT_PILL_LABEL_MAX_CHARS = 28;

export const SIGNATURE_TOGGLE_LABEL = 'Email Signature';

export const ATTACHMENT_DROPDOWN_UPLOAD_LABEL = 'Upload';

// Attachment-row icons. No paperclip asset exists in hostedassets, so we reuse
// the closest existing glyphs (see component imports). These keys keep the
// labels/icons declarative and out of the JSX.
export const ATTACHMENT_PILL_NEW_TAB_TARGET = '_blank';
